(() => {
  const preloadedVariantImages = new Set();

  class CardVariantPicker {
    constructor(root) {
      this.root = root;
      this.colorButtons = Array.from(root.querySelectorAll('[data-color-button]'));
      this.sizeSelect = root.querySelector('[data-card-size-select]');
      this.variantData = this.parseVariantData();
      this.colorIndex = this.getIndex(root.dataset.colorIndex);
      this.sizeIndex = this.getIndex(root.dataset.sizeIndex);
      this.soldOutLabel = root.dataset.soldOutLabel || 'Sold out';
      this.currentColor = root.dataset.initialColor || this.colorButtons[0]?.dataset.colorValue || null;
      this.submitButton = this.root.querySelector('.quick-add__submit');
      this.image = this.root.querySelector('.collection-item-image__wrapper img');
      this.defaultImage = this.image
        ? {
            src: this.image.getAttribute('src'),
            srcset: this.image.getAttribute('srcset'),
            alt: this.image.getAttribute('alt'),
            width: this.image.getAttribute('width'),
            height: this.image.getAttribute('height'),
          }
        : null;
      this.productLinks = Array.from(this.root.querySelectorAll('[data-product-link]'));
      this.priceWrapper = this.root.querySelector('[data-price-wrapper]');
      this.badge = this.root.querySelector('[data-product-badge]');
      this.saleBadgeText = root.dataset.saleBadgeText || 'Sale';
      this.saleBadgeColor = root.dataset.saleBadgeColor || '';
      this.defaultBadgeText = root.dataset.defaultBadgeText || '';
      this.defaultBadgeColor = root.dataset.defaultBadgeColor || '';

      if (!this.variantData.length || !this.sizeSelect || !this.colorButtons.length) return;

      this.sizeSelect.addEventListener('change', () => this.updateSelectedVariantState());
      this.preloadVariantImages();
      this.bindEvents();
      this.updateSizeOptions();
      this.updateSelectedVariantState();
    }

    parseVariantData() {
      const variantsData = this.root.getAttribute('data-variants');
      if (!variantsData) return [];
      try {
        return JSON.parse(variantsData);
      } catch (error) {
        console.error('Failed to parse card variant data', error);
        return [];
      }
    }

    getIndex(value) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }

    bindEvents() {
      this.colorButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.dataset.colorValue === this.currentColor) return;
          this.currentColor = button.dataset.colorValue;
          this.updateActiveColor(button);
          this.updateSizeOptions();
        });
      });
    }

    updateActiveColor(activeButton) {
      this.colorButtons.forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    updateSizeOptions() {
      if (this.colorIndex === null || this.sizeIndex === null) return;

      const previousSelection = this.sizeSelect.value;
      const matchingVariants = this.variantData.filter(
        (variant) => variant.options?.[this.colorIndex] === this.currentColor
      );

      if (!matchingVariants.length) return;

      this.sizeSelect.innerHTML = '';

      matchingVariants.forEach((variant) => {
        const sizeValue = variant.options?.[this.sizeIndex] || '';
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.available ? sizeValue : `${sizeValue} - ${this.soldOutLabel}`;
        option.disabled = !variant.available;
        option.dataset.sizeValue = sizeValue;
        option.dataset.colorValue = this.currentColor;
        this.sizeSelect.appendChild(option);
      });

      const fallbackVariant =
        matchingVariants.find((variant) => variant.id.toString() === previousSelection && variant.available) ||
        matchingVariants.find((variant) => variant.id.toString() === previousSelection) ||
        matchingVariants.find((variant) => variant.available) ||
        matchingVariants[0];

      if (fallbackVariant) {
        this.sizeSelect.value = fallbackVariant.id;
      }

      this.sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateSelectedVariantState() {
      if (!this.sizeSelect) return;
      const selectedVariant = this.variantData.find(
        (variant) => variant.id.toString() === this.sizeSelect.value
      );
      if (!selectedVariant) return;
      const onSale =
        selectedVariant.compare_at_price &&
        Number(selectedVariant.compare_at_price) > Number(selectedVariant.price);

      this.updateSubmitButton(selectedVariant.available);
      this.updateImage(selectedVariant.featured_media);
      this.updateProductLinks(selectedVariant.id);
      this.updatePrice(selectedVariant, onSale);
      this.updateBadge(onSale);
    }

    updateSubmitButton(isAvailable) {
      if (!this.submitButton) return;
      this.submitButton.disabled = !isAvailable;
      this.submitButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    }

    updateImage(media) {
      if (!this.image || !this.defaultImage) return;
      const imageData = media?.src ? media : this.defaultImage;

      if (imageData.src) {
        this.image.setAttribute('src', imageData.src);
      }
      if (imageData.srcset) {
        this.image.setAttribute('srcset', imageData.srcset);
      }
      if (imageData.alt !== undefined && imageData.alt !== null) {
        this.image.setAttribute('alt', imageData.alt);
      }
      if (imageData.width) {
        this.image.setAttribute('width', imageData.width);
      }
      if (imageData.height) {
        this.image.setAttribute('height', imageData.height);
      }
    }

    updateProductLinks(variantId) {
      if (!this.productLinks.length) return;
      this.productLinks.forEach((link) => {
        let baseUrl = link.dataset.baseUrl;
        if (!baseUrl) {
          const href = link.getAttribute('href') || '';
          baseUrl = href.split('?')[0];
        }
        if (!baseUrl) return;
        const separator = baseUrl.includes('?') ? '&' : '?';
        link.setAttribute('href', `${baseUrl}${separator}variant=${variantId}`);
      });
    }

    updatePrice(variant, onSaleOverride = null) {
      if (!this.priceWrapper) return;
      const priceElement = this.priceWrapper.querySelector('.price');
      if (!priceElement) return;

      const regularPrice = priceElement.querySelector('.price__regular .price-item--regular');
      const saleWrapper = priceElement.querySelector('.price__sale');
      const compareAtPrice = saleWrapper?.querySelector('.price-item--regular');
      const salePrice = saleWrapper?.querySelector('.price-item--sale');
      const onSale =
        typeof onSaleOverride === 'boolean'
          ? onSaleOverride
          : variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);

      priceElement.classList.toggle('price--on-sale', Boolean(onSale));
      priceElement.classList.toggle('price--sold-out', !variant.available);

      if (regularPrice) {
        regularPrice.textContent = variant.price_formatted || variant.price;
      }

      if (saleWrapper && onSale) {
        if (compareAtPrice) {
          compareAtPrice.textContent = variant.compare_at_price_formatted || variant.compare_at_price;
        }
        if (salePrice) {
          salePrice.textContent = variant.price_formatted || variant.price;
        }
      }
    }

    preloadVariantImages() {
      this.variantData.forEach(({ featured_media: media }) => {
        const src = media?.src;
        if (!src || preloadedVariantImages.has(src)) return;
        const img = new Image();
        img.src = src;
        preloadedVariantImages.add(src);
      });
    }

    updateBadge(onSale) {
      if (!this.badge) return;
      const isOnSale = Boolean(onSale);
      if (isOnSale) {
        this.badge.textContent = this.saleBadgeText;
        this.badge.style.background = this.saleBadgeColor || '';
        this.badge.removeAttribute('hidden');
        return;
      }

      if (this.defaultBadgeText) {
        this.badge.textContent = this.defaultBadgeText;
        this.badge.style.background = this.defaultBadgeColor || '';
        this.badge.removeAttribute('hidden');
        return;
      }

      this.badge.setAttribute('hidden', 'hidden');
      this.badge.textContent = this.saleBadgeText;
      this.badge.style.background = this.saleBadgeColor || '';
    }
  }

  const initPickers = (scope = document) => {
    scope
      .querySelectorAll('[data-card-variant-picker]:not([data-card-variant-picker-initialized])')
      .forEach((root) => {
        if (!root.querySelector('[data-color-button]') || !root.querySelector('[data-card-size-select]')) return;
        new CardVariantPicker(root);
        root.setAttribute('data-card-variant-picker-initialized', 'true');
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPickers());
  } else {
    initPickers();
  }

  window.initCardVariantPickers = initPickers;
})();
